import { Controller } from "@hotwired/stimulus"

// Release watch: landed price per site over time on a figure's page.
// Data: { labels: [dates], dates: [ISO dates], datasets: [{ label: site, data: [price|null] }] }.
// With `dates`, the x axis is true to time (six quiet months take six months'
// width, not one step), on a plain linear scale of timestamps.
//
// A figure's chart (ReleaseWatch::PriceHistory.for_offers) also says each
// line's store, colour and option, and lists `options` and `stores`. With a
// `legend` target the legend is its own: grouped by option (its lines in
// their store's badge colour) or by store (a colour per option), a
// checkbox on each line and one on each group for all its lines, so you can
// show only some options, some stores, or single lines. Hovering a line in
// it picks that line out on the chart. More than four groups slide along,
// like the options' shelf under it: four and the edge of the next, with arrows.
const PALETTE = [ "#facc15", "#60a5fa", "#34d399", "#f472b6", "#a78bfa", "#fb923c", "#22d3ee", "#f87171", "#a3e635", "#e879f9", "#94a3b8" ]
const GROUPING = "rw-history-grouping"

export default class extends Controller {
  static targets = [ "canvas", "legend" ]
  static values = { data: Object, unfiled: { type: String, default: "No option set" } }

  connect() {
    const { labels, dates, datasets, options = [], stores = [] } = this.dataValue
    this.options = options
    this.stores = stores
    this.grouped = datasets.some((d) => d.store)
    const times = dates?.map((d) => Date.parse(d))
    // Over a year or more (a store it was sold at years ago), dates say the year.
    const years = times && times[times.length - 1] - times[0] > 300 * 86400000
    const day = (t) => new Date(t).toLocaleDateString(undefined, { day: "2-digit", month: "short", ...(years && { year: "numeric" }), timeZone: "UTC" })
    const tick = (t) => years ? new Date(t).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" }) : day(t)
    this.chart = new window.Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: times ? undefined : labels,
        datasets: datasets.map((d, i) => {
          const color = this.grouped ? readable(d.color) : PALETTE[i % PALETTE.length]
          return {
            ...d,
            data: times ? d.data.map((y, j) => ({ x: times[j], y })) : d.data,
            borderColor: color,
            backgroundColor: color,
            stepped: true,
            spanGaps: true,
            pointRadius: 2,
            borderWidth: 2
          }
        })
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: !(this.grouped && this.hasLegendTarget), labels: { color: "#d1d5db", boxWidth: 12 } },
          tooltip: { callbacks: {
            title: (items) => times && items.length ? day(items[0].parsed.x) : undefined,
            label: (c) => c.parsed.y == null ? null : `${c.dataset.label}: $${c.parsed.y.toFixed(2)}`
          } }
        },
        scales: {
          x: times ? { type: "linear", min: times[0], max: times[times.length - 1], ticks: { color: "#9ca3af", maxTicksLimit: 8, callback: (v) => tick(v) }, grid: { color: "#374151" } }
                   : { ticks: { color: "#9ca3af" }, grid: { color: "#374151" } },
          y: { ticks: { color: "#9ca3af", callback: (v) => `$${v}` }, grid: { color: "#374151" } }
        }
      }
    })

    if (this.grouped && this.hasLegendTarget) {
      this.hiddenLines = new Set()
      let by = "option"
      try { by = localStorage.getItem(GROUPING) || by } catch {}
      this.buildLegend(this.options.length > 1 ? by : "option")
    }
  }

  disconnect() {
    this.chart?.destroy()
    this.resize?.disconnect()
  }

  // ---- the legend ----

  optionName(option) { return option ?? this.unfiledValue }

  buildLegend(by) {
    this.by = by
    const several = this.options.length > 1
    // The colours tell apart the lines in a group: grouped by option, its
    // stores, in their badge colours; grouped by store, its options, a colour
    // each (the same one in every store's group).
    this.chart.data.datasets.forEach((d) => {
      const color = several && by === "store" ? PALETTE[Math.max(0, this.options.indexOf(d.option)) % PALETTE.length] : readable(d.color)
      d.borderColor = d.backgroundColor = color
    })
    const lines = this.chart.data.datasets.map((d, i) => ({ d, i }))
    const storeOrder = (d) => this.stores.findIndex((s) => s.name === d.store)
    const optionOrder = (d) => this.options.indexOf(d.option)
    const groups = !several ? [ { lines } ]
      : by === "store"
        ? this.stores.map((s) => ({ title: s.name, lines: lines.filter(({ d }) => d.store === s.name).sort((a, b) => optionOrder(a.d) - optionOrder(b.d)) }))
        : this.options.map((o) => ({ title: this.optionName(o), lines: lines.filter(({ d }) => d.option === o).sort((a, b) => storeOrder(a.d) - storeOrder(b.d)) }))

    const parts = []
    const bar = el("div", "rw-hl-bar")
    if (several) {
      const toggle = el("div", "rw-hl-by")
      toggle.append(el("span", "rw-hl-by-label", "Group by"))
      ;[ [ "option", "Option" ], [ "store", "Store" ] ].forEach(([ key, label ]) => {
        const button = el("button", `rw-hl-by-choice${by === key ? " is-on" : ""}`, label)
        button.type = "button"
        button.setAttribute("aria-pressed", by === key)
        button.addEventListener("click", () => {
          try { localStorage.setItem(GROUPING, key) } catch {}
          this.buildLegend(key)
        })
        toggle.append(button)
      })
      bar.append(toggle)
    }
    const picks = el("div", "rw-hl-picks")
    ;[ [ "all", "Select all", () => this.hiddenLines.clear() ],
       [ "none", "Deselect all", () => this.chart.data.datasets.forEach((_, i) => this.hiddenLines.add(i)) ] ].forEach(([ key, label, act ]) => {
      const button = el("button", "rw-hl-pick", label)
      button.type = "button"
      button.dataset.pick = key
      button.addEventListener("click", () => { act(); this.apply() })
      picks.append(button)
    })
    bar.append(picks)
    parts.push(bar)

    const shown = groups.filter((g) => g.lines.length)
    const grid = el("div", "rw-hl-groups")
    if (shown.length > 4) this.slide(grid, bar, shown.length, several && by === "store" ? "Stores" : "Options")
    shown.forEach((g) => {
      const group = el("div", "rw-hl-group")
      const indexes = g.lines.map(({ i }) => i)
      if (g.title) {
        const head = el("label", "rw-hl-head")
        const box = el("input", "rw-hl-check")
        box.type = "checkbox"
        box.dataset.lines = indexes.join(",")
        box.addEventListener("change", () => {
          indexes.forEach((i) => box.checked ? this.hiddenLines.delete(i) : this.hiddenLines.add(i))
          this.apply()
        })
        head.append(box)
        head.append(el("span", "rw-hl-title", g.title))
        group.append(head)
      }
      const list = el("ul", "rw-hl-list")
      g.lines.forEach(({ d, i }) => {
        const item = el("label", "rw-hl-item")
        item.dataset.index = i
        const box = el("input", "rw-hl-check")
        box.type = "checkbox"
        box.dataset.lines = String(i)
        box.style.setProperty("accent-color", d.backgroundColor)
        box.addEventListener("change", () => { box.checked ? this.hiddenLines.delete(i) : this.hiddenLines.add(i); this.apply() })
        const last = [ ...d.data ].reverse().find((p) => (p?.y ?? p) != null)
        const price = last?.y ?? last
        const name = several && by === "store" ? this.optionName(d.option) : d.store
        const label = el("span", "rw-hl-store", name)
        label.title = name
        item.append(box, swatch(d.backgroundColor), label, el("span", "rw-hl-price", price == null ? "" : `$${price.toFixed(2)}`))
        item.addEventListener("mouseenter", () => this.spotlight(i))
        item.addEventListener("mouseleave", () => this.spotlight(null))
        const li = el("li")
        li.append(item)
        list.append(li)
      })
      group.append(list)
      grid.append(group)
    })
    parts.push(grid)
    this.legendTarget.replaceChildren(...parts)
    this.legendTarget.hidden = false
    this.apply()
  }

  // More than four groups: a row that slides along, a group at a time, the
  // count and arrows at the end of the legend's bar.
  slide(row, bar, total, noun) {
    row.classList.add("is-sliding")
    const count = el("span", "rw-shelf-count", `${total} ${noun.toLowerCase()}`)
    const prev = el("button", "rw-shelf-arrow")
    const next = el("button", "rw-shelf-arrow")
    prev.innerHTML = '<i class="fas fa-chevron-left"></i>'
    next.innerHTML = '<i class="fas fa-chevron-right"></i>'
    ;[ [ prev, "Back", -1 ], [ next, "More", 1 ] ].forEach(([ button, label, dir ]) => {
      button.type = "button"
      button.setAttribute("aria-label", label)
      button.addEventListener("click", () => row.scrollBy({ left: dir * step(), behavior: "smooth" }))
    })
    const arrows = el("span", "rw-shelf-arrows")
    arrows.append(prev, next)
    bar.append(count, arrows)
    const step = () => {
      const [ a, b ] = row.children
      return b ? b.offsetLeft - a.offsetLeft : row.clientWidth
    }
    const sync = () => {
      if (!row.clientWidth) return
      const max = row.scrollWidth - row.clientWidth
      const atStart = row.scrollLeft <= 2
      const atEnd = row.scrollLeft >= max - 2
      prev.disabled = atStart
      next.disabled = atEnd
      row.classList.toggle("has-more-left", !atStart)
      row.classList.toggle("has-more-right", !atEnd)
      const whole = Math.max(1, Math.floor((row.clientWidth + 20) / step()))
      const first = atEnd ? total - whole + 1 : Math.round(row.scrollLeft / step()) + 1
      const last = atEnd ? total : Math.min(total, first + whole - 1)
      count.textContent = first === last ? `${noun.slice(0, -1)} ${first} of ${total}` : `${noun} ${first}–${last} of ${total}`
    }
    row.addEventListener("scroll", sync)
    this.resize?.disconnect()
    this.resize = new ResizeObserver(sync)
    this.resize.observe(row)
  }

  // Hovering a legend line: it drawn thick and the rest faded.
  spotlight(index) {
    this.chart.data.datasets.forEach((d, i) => {
      d.borderColor = index == null || i === index ? d.backgroundColor : `${d.backgroundColor}2e`
      d.borderWidth = i === index ? 4 : 2
    })
    this.chart.update("none")
  }

  // Each line shown unless unticked; a group's box ticked when all its lines
  // are, half when some are.
  apply() {
    this.chart.data.datasets.forEach((_, i) => this.chart.setDatasetVisibility(i, !this.hiddenLines.has(i)))
    this.legendTarget.querySelectorAll(".rw-hl-check").forEach((box) => {
      const lines = box.dataset.lines.split(",").map(Number)
      const off = lines.filter((i) => this.hiddenLines.has(i)).length
      box.checked = off === 0
      box.indeterminate = off > 0 && off < lines.length
      box.closest(".rw-hl-item, .rw-hl-head")?.classList.toggle("is-off", off === lines.length)
    })
    this.legendTarget.querySelector('[data-pick="all"]').disabled = this.hiddenLines.size === 0
    this.legendTarget.querySelector('[data-pick="none"]').disabled = this.hiddenLines.size === this.chart.data.datasets.length
    this.chart.update()
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}

// A short stretch of line in a line's colour, for the legend.
function swatch(color) {
  const mark = el("span", "rw-line-sample")
  mark.style.background = color
  return mark
}

// A colour as "#rrggbb", whatever CSS form it came in (a badge's is hsl()).
function hex(color) {
  const ctx = document.createElement("canvas").getContext("2d")
  ctx.fillStyle = "#94a3b8"
  ctx.fillStyle = color || "#94a3b8"
  return ctx.fillStyle
}

function rgb(color) {
  const h = hex(color)
  return [ 1, 3, 5 ].map((i) => parseInt(h.slice(i, i + 2), 16))
}

// A store's colour, lifted toward white until it shows on the dark chart
// (a black logo's store would vanish otherwise).
function readable(color) {
  const c = rgb(color)
  const light = 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]
  const t = Math.max(0, (125 - light) / (255 - light))
  return "#" + c.map((v) => Math.round(v + (255 - v) * t).toString(16).padStart(2, "0")).join("")
}
